import { FC, useCallback, useEffect, useState } from 'react';
import KpiCard from '@/components/admin/KpiCard';
import BarChartCard from '@/components/admin/charts/BarChartCard';
import RankTableCard from '@/components/admin/charts/RankTableCard';
import { seopService } from '@/services/seop.service';
import type { SeopCalc, SeopMetric } from '@/types/seop';
import { useSeopMonth } from '@/hooks/useSeopMonth';
import { fmtHours, fmtUnits, fmtUtil, seopNum } from '@/utils/seopFormat';

/** Não-finito vai para o topo da fila — é a sentinela 999 do motor. */
const ordenaPorUtilizacao = (a: SeopMetric, b: SeopMetric): number => {
  const ua = seopNum(a.util);
  const ub = seopNum(b.util);
  return (Number.isFinite(ub) ? ub : 999) - (Number.isFinite(ua) ? ua : 999);
};

const SeopDashboardPage: FC = () => {
  const { month } = useSeopMonth();
  const [calc, setCalc] = useState<SeopCalc | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (mes: number) => {
    setIsLoading(true);
    setError(null);
    try {
      setCalc(await seopService.getCalc(mes));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro desconhecido');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(month);
  }, [month, fetchData]);

  if (isLoading) {
    return <div className="p-6 lg:p-10 text-content-muted">Calculando o período…</div>;
  }

  if (error || !calc) {
    return (
      <div className="p-6 lg:p-10">
        <div className="rounded-lg border border-theme-danger/40 bg-theme-danger/10 p-4 text-content-primary">
          Não foi possível calcular o período. {error}
        </div>
      </div>
    );
  }

  const { totals, metrics } = calc;
  const maxUtil = seopNum(totals.max_util);
  const zeroCap = totals.zero_capacity_with_load;
  const prioridades = [...metrics].sort(ordenaPorUtilizacao).slice(0, 8);

  return (
    <div className="p-6 lg:p-10">
      {zeroCap > 0 && (
        <div
          data-testid="seop-callout-capacidade-zero"
          className="mb-6 rounded-lg border border-theme-danger/40 bg-theme-danger/10 p-4 text-content-primary"
        >
          <strong>
            {zeroCap} recurso{zeroCap === 1 ? '' : 's'}
          </strong>{' '}
          {zeroCap === 1 ? 'tem' : 'têm'} demanda, mas capacidade zero no período. Cadastre a
          capacidade ou replique um mês válido — sem isso a utilização fica ∞ e o balanceamento
          não tem para onde mover carga.
        </div>
      )}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(15rem,1fr))] gap-4">
        <KpiCard
          icon="warning"
          value={totals.overloaded}
          label="Recursos sobrecarregados"
          sublabel="Utilização > 100%"
        />
        <KpiCard
          icon="speed"
          // `Math.max()` de lista vazia é -Infinity em JS; o motor já devolve 0
          // quando não há utilização finita nenhuma.
          value={Number.isFinite(maxUtil) ? fmtUtil(maxUtil) : '∞'}
          label="Maior utilização"
          sublabel="Gargalo do período"
        />
        <KpiCard
          icon="inventory_2"
          value={fmtUnits(totals.total_lots)}
          label="Demanda total"
          sublabel={totals.total_lots === 1 ? 'lote' : 'lotes'}
        />
        <KpiCard
          icon="schedule"
          value={`${fmtHours(totals.total_load_hours)} h`}
          label="Carga calculada"
          sublabel="com arredondamento de lotes"
        />
        <KpiCard
          icon="factory"
          value={`${fmtHours(totals.total_capacity_hours)} h`}
          label="Capacidade efetiva"
          sublabel="dias × turnos × horas × OEE − deduções"
        />
      </div>

      {/* Um embaixo do outro (26/10/2026): o gráfico na largura toda, com o % na ponta de cada barra. */}
      <div className="mt-6 grid grid-cols-1 gap-4">
        <BarChartCard
          title="Utilização por recurso"
          subtitle={`Meta operacional: ${fmtUtil(calc.bands.balanceado_min)}–${fmtUtil(calc.bands.atencao_min)}. A linha tracejada marca 100% da capacidade.`}
          data={metrics.map((m) => ({
            name: m.name,
            // Satura em 160%, como no app: acima disso a barra pararia de informar e
            // só distorceria a escala dos outros recursos.
            util: Number.isFinite(seopNum(m.util))
              ? Math.min(Math.round(seopNum(m.util) * 1000) / 10, 160)
              : 160,
            // O texto diz o valor de verdade (∞ quando não tem capacidade), mesmo com a barra saturada.
            utilReal: seopNum(m.util),
          }))}
          dataKey="util"
          categoryKey="name"
          layout="vertical"
          height={Math.max(320, metrics.length * 32)}
          valueLabel={{ key: 'utilReal', format: fmtUtil }}
          referenceValue={100}
          referenceLabel="100%"
        />

        <RankTableCard
          title="Prioridades do período"
          subtitle="Os oito recursos com maior utilização"
          rows={prioridades}
          columns={[
            { key: 'name', label: 'Recurso' },
            {
              key: 'util',
              label: 'Util.',
              align: 'right',
              render: (m: SeopMetric) => fmtUtil(seopNum(m.util)),
            },
            // A recomendação vem pronta do motor — reproduzi-la aqui criaria uma
            // segunda fonte de verdade para a mesma regra.
            { key: 'recommendation', label: 'Recomendação' },
          ]}
        />
      </div>
    </div>
  );
};

export default SeopDashboardPage;
